import React, { createContext, useState, useContext, useEffect } from 'react';
import { neon } from '@neondatabase/serverless';

// Get the database URL from environment variables or use direct string for development
const getDatabaseUrl = () => {
  return 'postgres://neondb_owner:npg_Ub9EiNceZ3ro@ep-divine-sun-a18caqng-pooler.ap-southeast-1.aws.neon.tech/neondb';
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [databaseUrl] = useState(() => getDatabaseUrl());

  // Initialize database and check for existing session
  useEffect(() => {
    const initDatabase = async () => {
      try {
        if (!databaseUrl) {
          throw new Error('No database URL available');
        }
        
        const sql = neon(databaseUrl, { ssl: false });
        
        // Create members table if it doesn't exist
        await sql`
          CREATE TABLE IF NOT EXISTS members (
            id SERIAL PRIMARY KEY,
            username TEXT UNIQUE NOT NULL,
            email TEXT UNIQUE NOT NULL,
            password_hash TEXT NOT NULL,
            created_at TIMESTAMP WITH TIME ZONE DEFAULT (CURRENT_TIMESTAMP AT TIME ZONE 'Asia/Taipei')
          )
        `;

        // Check for existing session
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
      } catch (err) {
        console.error('Error initializing database:', err);
      } finally {
        setLoading(false);
      }
    };

    initDatabase();
  }, [databaseUrl]);

  const login = async (email, password) => {
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // In a real app, you would hash the password before comparing
      const result = await sql`
        SELECT id, username, email 
        FROM members 
        WHERE email = ${email} AND password_hash = ${password}
      `;
      
      if (result && result.length > 0) {
        const userData = result[0];
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true };
      } else {
        return { success: false, error: 'Invalid email or password' };
      }
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: err.message };
    }
  };

  const register = async (username, email, password) => {
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Check if user already exists
      const existingUser = await sql`
        SELECT id FROM members WHERE email = ${email} OR username = ${username}
      `;
      
      if (existingUser && existingUser.length > 0) {
        return { success: false, error: 'Username or email already exists' };
      }
      
      // Insert new user
      // In a real app, you would hash the password before storing
      await sql`
        INSERT INTO members (username, email, password_hash)
        VALUES (${username}, ${email}, ${password})
      `;
      
      return { success: true };
    } catch (err) {
      console.error('Registration error:', err);
      return { success: false, error: err.message };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 